/**
 * Edit-mode singleton store.
 *
 * Tracks whether the inline preview editor is active. The flag drives:
 *   1. The i18next post-processor in ``apps/web/src/i18n/index.ts``,
 *      which wraps every editable string in invisible markers when
 *      active.
 *   2. The DOM observer + Portal overlay rendered by
 *      ``EditOverlayLayer``.
 *
 * Active iff:
 *   - URL contains ``?_edit=1``
 *   - User is authenticated
 *   - User has a ``role === "admin"`` membership in the tenant
 *   - Resolved shell is ``tenant`` (no platform / platform-admin)
 *
 * Implemented as a tiny event-emitter + react hook so the i18next
 * post-processor (non-React) can read the flag without pulling
 * Zustand/Jotai into the bundle.
 *
 * Hit registry: the post-processor wraps editable strings with
 * invisible markers and stores the (ns, key) pair here under a numeric
 * id encoded in the marker. The DOM observer reads the id back and
 * looks up the pair. Doing it this way means the DOM contains *only*
 * zero-width / invisible characters — no human-visible artefacts can
 * leak through into the rendered page even if a font / browser refuses
 * to suppress one of them.
 */

type Listener = () => void;

export interface EditableHit {
  ns: string;
  key: string;
}

class EditModeStore {
  private active = false;
  private listeners = new Set<Listener>();
  private hitsByKey = new Map<string, number>();
  private hitsById = new Map<number, EditableHit>();
  private nextId = 1;

  get isActive(): boolean {
    return this.active;
  }

  set(active: boolean): void {
    if (this.active === active) return;
    this.active = active;
    if (!active) this.resetHits();
    for (const listener of this.listeners) listener();
  }

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): boolean => this.active;

  /** Register a (ns, key) pair, returning a stable id. Repeated calls
   * for the same pair return the same id (dedupe — keeps the registry
   * bounded by unique editable keys, not by render count). */
  registerHit(ns: string, key: string): number {
    const k = `${ns}|${key}`;
    const existing = this.hitsByKey.get(k);
    if (existing !== undefined) return existing;
    const id = this.nextId++;
    this.hitsByKey.set(k, id);
    this.hitsById.set(id, { ns, key });
    return id;
  }

  lookupHit(id: number): EditableHit | undefined {
    return this.hitsById.get(id);
  }

  resetHits(): void {
    this.hitsByKey.clear();
    this.hitsById.clear();
    this.nextId = 1;
  }
}

export const editModeStore = new EditModeStore();

/** Read-only check usable from non-React modules (i18next post-processor). */
export function isEditModeActive(): boolean {
  return editModeStore.isActive;
}

// --- Invisible marker encoding -----------------------------------------
//
// Open delim: U+2063 INVISIBLE SEPARATOR
// Close delim: U+2063 U+2063 (two of them; the open + 20-bit id form a
// distinct prefix so a single ⁣ inside the value text would never
// match the close pattern accidentally).
// ID encoding: 20 bits, LSB first, ZWSP (U+200B) = 0, ZWNJ (U+200C) = 1.
// 20 bits = 1,048,576 unique editable keys per session — far above any
// realistic page count.
//
// All five characters (U+2063, U+200B, U+200C) are zero-width / invisible
// across modern browsers; no font fallback turns them into visible glyphs
// the way control-pair encoded text would.

export const MARKER_OPEN = "⁣";
export const MARKER_CLOSE = "⁣⁣";
export const MARKER_BIT0 = "​";
export const MARKER_BIT1 = "‌";
export const MARKER_ID_BITS = 20;

export function encodeMarkerId(id: number): string {
  let s = "";
  for (let i = 0; i < MARKER_ID_BITS; i++) {
    s += (id >> i) & 1 ? MARKER_BIT1 : MARKER_BIT0;
  }
  return s;
}

export function decodeMarkerId(encoded: string): number {
  let id = 0;
  for (let i = 0; i < MARKER_ID_BITS; i++) {
    if (encoded[i] === MARKER_BIT1) id |= 1 << i;
  }
  return id;
}

/**
 * Regex that finds an editable marker in a text node.
 *
 * Pattern: ``⁣ <20 zero-width id bits> ⁣ <value> ⁣⁣``
 *
 * Capture groups:
 *   1. The 20-char id (use ``decodeMarkerId``)
 *   2. The visible value text rendered between the open and close delims
 */
/**
 * Text bez editorových značek (zůstane jen viditelná hodnota). Značky patří jen
 * do textových uzlů, které čte překryv s tužkami; do titulku záložky, ``aria-label``
 * a ``alt`` se nesmí dostat (KAN-1816).
 */
export function stripEditMarkers(text: string): string {
  if (!text.includes(MARKER_OPEN)) return text;
  return text.replace(
    new RegExp(MARKER_RE.source, "g"),
    (_m, _id: string, value: string) => value,
  );
}

export const MARKER_RE = /⁣([​‌]{20})⁣([\s\S]*?)⁣⁣/g;
